import React, { useMemo, useState, useCallback, useRef } from 'react';
import Plot from 'react-plotly.js';
import { useData } from '../../context/DataContext';
import { disperseAnnotations } from '../../utils/plotUtils';

const COMPARISON_ORDER = ['vitro_vs_vivo', 'vivo_vs_p6', 'gcb_vs_p0', 'p0_vs_p6', 'p6_vs_taz', 'p0_vs_taz'];

// Caveats for specific comparisons (batch effects, low n)
const COMPARISON_CAVEATS = {
  vitro_vs_vivo: {
    color: '#e67e22',
    text: 'Cross-dataset comparison (Chan \'22 vs Lightcap \'21). N=2 for A20 vitro. Batch effects may confound results — interpret with caution.',
  },
  vivo_vs_p6: {
    color: '#e67e22',
    text: 'Cross-dataset comparison (Lightcap \'21 A20 vs Isshiki \'25 EmBO). Batch effects likely confound results — use for exploratory analysis only.',
  },
};

const FDR_OPTIONS = [0.001, 0.01, 0.05, 0.1];

// Gene signatures for quick highlight (panel name -> button label + color)
const SIGNATURES = [
  { panel: 'GCB-DLBCL Biology', label: 'GCB-DLBCL', color: '#c0392b' },
  { panel: 'GC Polarization', label: 'GC Polar.', color: '#2471a3' },
  { panel: 'Immune Evasion', label: 'Immune', color: '#27ae60' },
  { panel: 'DLBCL-IQ GCB', label: 'IQ-GCB', color: '#e67e22' },
  { panel: 'DLBCL-IQ ABC', label: 'IQ-ABC', color: '#8e44ad' },
];

export default function VolcanoPlot({ onSelectGene }) {
  const { data, resolveGene } = useData();
  const [selectedComp, setSelectedComp] = useState('vivo_vs_p6');
  const [fcCutoff, setFcCutoff] = useState(1);
  const [fdrCutoff, setFdrCutoff] = useState(0.05);
  const [exprCutoff, setExprCutoff] = useState(1);
  const [toggledLabels, setToggledLabels] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [activeSignature, setActiveSignature] = useState(null);
  const searchRef = useRef(null);

  const handleCompChange = useCallback((e) => {
    setSelectedComp(e.target.value);
    setToggledLabels(new Set());
    setSearchResult(null);
  }, []);

  const compData = data?.deResults?.[selectedComp];
  const comparisons = data?.deResults || {};

  // Get genes for active signature panel
  const signatureGenes = useMemo(() => {
    if (!activeSignature || !data?.panels) return [];
    const panel = data.panels[activeSignature.panel];
    if (!panel) return [];
    const genes = [];
    for (const subcatGenes of Object.values(panel)) {
      genes.push(...subcatGenes);
    }
    return [...new Set(genes)];
  }, [activeSignature, data]);

  const plotData = useMemo(() => {
    if (!compData) return null;

    const allGenes = Object.keys(compData.genes);
    const genes = [];
    const log2fc = [];
    const fdr = [];
    const meanA = [];
    const meanB = [];

    for (const g of allGenes) {
      const d = compData.genes[g];
      const tpmA = Math.pow(2, d.meanA) - 1;
      const tpmB = Math.pow(2, d.meanB) - 1;
      if (Math.max(tpmA, tpmB) < exprCutoff) continue;

      genes.push(g);
      log2fc.push(d.log2FC);
      fdr.push(d.fdr);
      meanA.push(d.meanA);
      meanB.push(d.meanB);
    }

    const minNonzero = Math.min(...fdr.filter(f => f > 0)) * 0.1 || 1e-16;
    const negLogFdr = fdr.map(f => -Math.log10(f > 0 ? f : minNonzero));

    const sigUp = [], sigDown = [], ns = [];
    for (let i = 0; i < genes.length; i++) {
      if (fdr[i] < fdrCutoff && log2fc[i] > fcCutoff) sigUp.push(i);
      else if (fdr[i] < fdrCutoff && log2fc[i] < -fcCutoff) sigDown.push(i);
      else ns.push(i);
    }

    let nsSubset = ns;
    if (ns.length > 4000) {
      const step = Math.ceil(ns.length / 4000);
      nsSubset = ns.filter((_, i) => i % step === 0);
    }

    const scores = genes.map((_, i) => negLogFdr[i] * Math.abs(log2fc[i]));
    const sigAll = [...sigUp, ...sigDown];
    const sigSorted = [...sigAll].sort((a, b) => scores[b] - scores[a]);
    const topGenes = sigSorted.slice(0, 15).map(i => ({
      gene: genes[i], x: log2fc[i], y: negLogFdr[i], idx: i,
    }));

    return { genes, log2fc, fdr, negLogFdr, sigUp, sigDown, nsSubset, topGenes };
  }, [compData, fcCutoff, fdrCutoff, exprCutoff]);

  // Gene search
  const handleSearchInput = useCallback((e) => {
    const q = e.target.value;
    setSearchQuery(q);
    setSearchResult(null);
    if (q.length >= 2 && data?.geneList) {
      const lower = q.toLowerCase();
      const matches = data.geneList.filter(g => g.toLowerCase().startsWith(lower)).slice(0, 8);
      setSuggestions(matches);
    } else {
      setSuggestions([]);
    }
  }, [data]);

  const doSearch = useCallback((geneName) => {
    const resolved = resolveGene(geneName);
    if (resolved && compData?.genes[resolved]) {
      setSearchResult({ gene: resolved, found: true });
      setSearchQuery(resolved);
    } else if (resolved) {
      setSearchResult({ gene: resolved, found: false });
      setSearchQuery(resolved);
    } else {
      setSearchResult({ gene: geneName, found: false });
    }
    setSuggestions([]);
  }, [resolveGene, compData]);

  const handleSearchKey = useCallback((e) => {
    if (e.key === 'Enter') doSearch(searchQuery);
  }, [searchQuery, doSearch]);

  const handleSuggestionClick = useCallback((gene) => {
    doSearch(gene);
    searchRef.current?.blur();
  }, [doSearch]);

  const handleSignatureClick = useCallback((sig) => {
    setActiveSignature(prev =>
      prev?.panel === sig.panel ? null : sig
    );
  }, []);

  const handleClick = useCallback((e) => {
    if (e.points && e.points[0]) {
      const pt = e.points[0];
      const gene = pt.text || pt.hovertext;
      if (gene) {
        setToggledLabels(prev => {
          const next = new Set(prev);
          if (next.has(gene)) {
            next.delete(gene);
            if (onSelectGene) onSelectGene(gene);
          } else {
            next.add(gene);
          }
          return next;
        });
      }
    }
  }, [onSelectGene]);

  if (!data?.deResults) return <div className="plot-placeholder">Loading volcano data...</div>;

  const compOptions = COMPARISON_ORDER.filter(id => comparisons[id]);

  if (!plotData) return <div className="plot-placeholder">No data for selected comparison</div>;

  const { genes, log2fc, fdr: fdrArr, negLogFdr, sigUp, sigDown, nsSubset, topGenes } = plotData;

  // Helper: get plot coords for a gene (from filtered array or raw compData)
  const getGeneCoords = (gene) => {
    const idx = genes.indexOf(gene);
    if (idx !== -1) {
      return { x: log2fc[idx], y: negLogFdr[idx], fdr: fdrArr[idx] };
    }
    const d = compData.genes[gene];
    if (!d) return null;
    const minNz = Math.min(...fdrArr.filter(f => f > 0)) * 0.1 || 1e-16;
    const y = -Math.log10(d.fdr > 0 ? d.fdr : minNz);
    return { x: d.log2FC, y, fdr: d.fdr };
  };

  const traces = [
    {
      type: 'scattergl',
      mode: 'markers',
      x: nsSubset.map(i => log2fc[i]),
      y: nsSubset.map(i => negLogFdr[i]),
      marker: { size: 3, color: '#bbb', opacity: 0.4 },
      hovertext: nsSubset.map(i => genes[i]),
      hoverinfo: 'text',
      name: `NS (${nsSubset.length})`,
      showlegend: true,
    },
    {
      type: 'scattergl',
      mode: 'markers',
      x: sigUp.map(i => log2fc[i]),
      y: sigUp.map(i => negLogFdr[i]),
      marker: { size: 5, color: '#c0392b', opacity: 0.7 },
      hovertext: sigUp.map(i =>
        `${genes[i]}<br>FC: ${log2fc[i].toFixed(2)}<br>FDR: ${fdrArr[i].toExponential(2)}`),
      hoverinfo: 'text',
      name: `Up in ${compData.labelA} (${sigUp.length})`,
    },
    {
      type: 'scattergl',
      mode: 'markers',
      x: sigDown.map(i => log2fc[i]),
      y: sigDown.map(i => negLogFdr[i]),
      marker: { size: 5, color: '#2471a3', opacity: 0.7 },
      hovertext: sigDown.map(i =>
        `${genes[i]}<br>FC: ${log2fc[i].toFixed(2)}<br>FDR: ${fdrArr[i].toExponential(2)}`),
      hoverinfo: 'text',
      name: `Up in ${compData.labelB} (${sigDown.length})`,
    },
  ];

  // Signature highlight trace — colored markers for panel genes
  if (activeSignature && signatureGenes.length > 0) {
    const sigColor = activeSignature.color;
    const hx = [], hy = [], htext = [];
    for (const g of signatureGenes) {
      const coords = getGeneCoords(g);
      if (coords) {
        hx.push(coords.x);
        hy.push(coords.y);
        htext.push(`<b>${g}</b><br>FC: ${coords.x.toFixed(2)}<br>FDR: ${coords.fdr.toExponential(2)}`);
      }
    }
    if (hx.length > 0) {
      traces.push({
        type: 'scatter',
        mode: 'markers',
        x: hx,
        y: hy,
        marker: {
          size: 9,
          color: sigColor,
          opacity: 0.9,
          line: { width: 1.5, color: '#fff' },
        },
        hovertext: htext,
        hoverinfo: 'text',
        name: activeSignature.label,
        showlegend: true,
      });
    }
  }

  // Searched gene — diamond marker
  if (searchResult?.found) {
    const coords = getGeneCoords(searchResult.gene);
    if (coords) {
      traces.push({
        type: 'scatter',
        mode: 'markers',
        x: [coords.x],
        y: [coords.y],
        marker: {
          size: 14,
          color: '#f1c40f',
          line: { width: 2, color: '#1a1a1a' },
          symbol: 'diamond',
        },
        hovertext: [`<b>${searchResult.gene}</b><br>FC: ${coords.x.toFixed(2)}<br>FDR: ${coords.fdr.toExponential(2)}`],
        hoverinfo: 'text',
        name: searchResult.gene,
        showlegend: false,
      });
    }
  }

  // Build set of genes to label
  const allLabeled = new Set(topGenes.map(t => t.gene));
  toggledLabels.forEach(g => allLabeled.add(g));
  if (searchResult?.found) allLabeled.add(searchResult.gene);
  // Label signature genes too
  if (activeSignature) {
    for (const g of signatureGenes) {
      if (compData.genes[g]) allLabeled.add(g);
    }
  }

  // Axis ranges for smart initial label direction
  const xDataMin = Math.min(...log2fc);
  const xDataMax = Math.max(...log2fc);
  const yDataMax = Math.max(...negLogFdr);
  const xMid = (xDataMin + xDataMax) / 2;
  const yTop80 = yDataMax * 0.8;

  // Axis domains for dispersion (what Plotly will auto-range to, roughly)
  const xDomain = [xDataMin - 0.8, xDataMax + 0.8];
  const yDomain = [0, yDataMax * 1.08];

  const rawAnnotations = [];
  for (const gene of allLabeled) {
    const coords = getGeneCoords(gene);
    if (!coords) continue;
    const ax = coords.x > xMid ? -22 : 22;
    const ay = coords.y > yTop80 ? 22 : -18;
    const isSearched = searchResult?.gene === gene;
    const isSigGene = activeSignature && signatureGenes.includes(gene);
    rawAnnotations.push({
      x: coords.x,
      y: coords.y,
      text: `<b>${gene}</b>`,
      showarrow: true,
      arrowhead: 0,
      arrowcolor: isSearched ? '#e67e22' : isSigGene ? activeSignature.color : '#999',
      arrowwidth: isSearched ? 2 : 1,
      ax,
      ay,
      font: {
        size: isSearched ? 12 : 10,
        color: isSearched ? '#c0392b' : isSigGene ? activeSignature.color : '#1a1a1a',
      },
      bgcolor: isSearched ? 'rgba(241,196,15,0.3)' : 'rgba(255,255,255,0.85)',
      bordercolor: isSearched ? '#e67e22' : undefined,
      borderwidth: isSearched ? 1 : 0,
      borderpad: 3,
    });
  }

  // Disperse labels to prevent overlap
  const plotWidth = 880, plotHeight = 620;
  const margin = { l: 65, r: 100, t: 50, b: 55 };
  const annotations = disperseAnnotations(rawAnnotations, plotWidth, plotHeight, margin, xDomain, yDomain);

  // Threshold lines
  const yMax = yDataMax + 1;
  const xMin = xDataMin - 1;
  const xMax = xDataMax + 1;
  const shapes = [
    { type: 'line', x0: -fcCutoff, x1: -fcCutoff, y0: 0, y1: yMax,
      line: { color: '#aaa', width: 1, dash: 'dash' }, xref: 'x', yref: 'y' },
    { type: 'line', x0: fcCutoff, x1: fcCutoff, y0: 0, y1: yMax,
      line: { color: '#aaa', width: 1, dash: 'dash' }, xref: 'x', yref: 'y' },
    { type: 'line', x0: xMin, x1: xMax,
      y0: -Math.log10(fdrCutoff), y1: -Math.log10(fdrCutoff),
      line: { color: '#aaa', width: 1, dash: 'dash' }, xref: 'x', yref: 'y' },
  ];

  return (
    <div className="volcano-container">
      <div className="volcano-controls">
        <div className="volcano-control-group">
          <label>Comparison</label>
          <select value={selectedComp} onChange={handleCompChange}>
            {compOptions.map(id => (
              <option key={id} value={id}>{comparisons[id].label}</option>
            ))}
          </select>
        </div>

        <div className="volcano-control-group">
          <label>|log₂FC| &ge; {fcCutoff}</label>
          <input
            type="range"
            min="0" max="5" step="0.5"
            value={fcCutoff}
            onChange={e => setFcCutoff(Number(e.target.value))}
          />
        </div>

        <div className="volcano-control-group">
          <label>FDR &lt;</label>
          <select value={fdrCutoff} onChange={e => setFdrCutoff(Number(e.target.value))}>
            {FDR_OPTIONS.map(v => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
        </div>

        <div className="volcano-control-group">
          <label>Min TPM &ge; {exprCutoff}</label>
          <input
            type="range"
            min="0" max="10" step="0.5"
            value={exprCutoff}
            onChange={e => setExprCutoff(Number(e.target.value))}
          />
        </div>

        <div className="volcano-control-group volcano-search">
          <label>Find Gene</label>
          <div className="volcano-search-wrapper">
            <input
              ref={searchRef}
              type="text"
              value={searchQuery}
              onChange={handleSearchInput}
              onKeyDown={handleSearchKey}
              placeholder="e.g. Bcl6"
              className="volcano-search-input"
            />
            {suggestions.length > 0 && (
              <ul className="volcano-suggestions">
                {suggestions.map(g => (
                  <li key={g} onMouseDown={() => handleSuggestionClick(g)}>
                    <em>{g}</em>
                    {compData.genes[g] && (
                      <span className="suggestion-stats">
                        FC: {compData.genes[g].log2FC.toFixed(1)}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
          {searchResult && !searchResult.found && (
            <span className="volcano-search-miss">Not in this comparison</span>
          )}
        </div>
      </div>

      <div className="volcano-signatures">
        <span className="volcano-sig-label">Highlight:</span>
        {SIGNATURES.map(sig => (
          <button
            key={sig.panel}
            className={`volcano-sig-btn ${activeSignature?.panel === sig.panel ? 'active' : ''}`}
            style={{
              '--sig-color': sig.color,
              borderColor: activeSignature?.panel === sig.panel ? sig.color : '#ccc',
              background: activeSignature?.panel === sig.panel ? sig.color + '18' : '#fff',
              color: activeSignature?.panel === sig.panel ? sig.color : '#555',
            }}
            onClick={() => handleSignatureClick(sig)}
          >
            {sig.label}
          </button>
        ))}
        {activeSignature && (
          <span className="volcano-sig-count">
            {signatureGenes.filter(g => compData.genes[g]).length}/{signatureGenes.length} in DE
          </span>
        )}
      </div>

      {COMPARISON_CAVEATS[selectedComp] && (
        <div className="volcano-caveat" style={{
          background: COMPARISON_CAVEATS[selectedComp].color + '15',
          border: `1px solid ${COMPARISON_CAVEATS[selectedComp].color}40`,
          borderRadius: 6,
          padding: '6px 12px',
          margin: '4px 0 8px',
          fontSize: 12,
          color: '#555',
          lineHeight: 1.4,
        }}>
          <strong style={{ color: COMPARISON_CAVEATS[selectedComp].color }}>Caveat: </strong>
          {COMPARISON_CAVEATS[selectedComp].text}
        </div>
      )}

      <Plot
        data={traces}
        layout={{
          width: plotWidth,
          height: plotHeight,
          paper_bgcolor: '#ffffff',
          plot_bgcolor: '#ffffff',
          margin,
          xaxis: {
            title: {
              text: `log₂ FC (${compData.labelA} vs ${compData.labelB})`,
              font: { size: 12, color: '#333' },
            },
            tickfont: { size: 10, color: '#555' },
            gridcolor: '#eee',
            zeroline: false,
            showline: true,
            linecolor: '#ccc',
          },
          yaxis: {
            title: { text: '-log₁₀(FDR)', font: { size: 12, color: '#333' } },
            tickfont: { size: 10, color: '#555' },
            gridcolor: '#eee',
            zeroline: false,
            showline: true,
            linecolor: '#ccc',
          },
          shapes,
          annotations,
          legend: {
            x: 0.02, y: 0.02,
            xanchor: 'left',
            yanchor: 'bottom',
            font: { size: 10, color: '#333' },
            bgcolor: 'rgba(255,255,255,0.9)',
            bordercolor: '#ddd',
            borderwidth: 1,
          },
          hovermode: 'closest',
        }}
        config={{ displayModeBar: false, responsive: true }}
        onClick={handleClick}
      />
      <p className="volcano-hint">Click a point to label it, click again to view gene detail</p>
    </div>
  );
}
